import { type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import type { StatusTone } from '../../lib/status';

export interface NoticeProps {
  readonly tone: StatusTone;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly action?: ReactNode;
  readonly icon?: ReactNode;
  readonly className?: string;
}

/**
 * A quiet inline message inside a panel or a form — an explanation, a
 * precondition, a "this will also do X".
 *
 * On day it is a tinted card in the status hue; on night the tint resolves to
 * the panel itself and the message is carried by a 1 px border and the status
 * ink. That is deliberate: a pastel wash on `#121517` is either invisible or
 * garish, and the night theme has one fewer level of surface by design.
 */
export function Notice({ tone, title, description, action, icon, className }: NoticeProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-control border p-3',
        tone === 'success' && 'border-status-success-ink bg-status-success-surface',
        tone === 'warning' && 'border-status-warning-ink bg-status-warning-surface',
        tone === 'danger' && 'border-status-danger-ink bg-status-danger-surface',
        tone === 'info' && 'border-status-info-ink bg-status-info-surface',
        tone === 'neutral' && 'border-border bg-status-neutral-surface',
        tone === 'neutral-strong' && 'border-ink-subtle bg-surface-sunk',
        className,
      )}
    >
      {icon !== undefined ? (
        <span aria-hidden="true" className="mt-px inline-flex shrink-0">
          {icon}
        </span>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="text-subject text-ink">{title}</p>
        {description !== undefined ? (
          <p className="mt-0.5 text-body-compact text-ink-muted">{description}</p>
        ) : null}
      </div>
      {action !== undefined ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
